import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useApiClient } from "../api/ApiClientContext";
import { ApiError } from "../api/types";
import type { Me } from "../api/types";

type AuthStatus = "loading" | "authenticated" | "unauthenticated" | "error";

/**
 * GET /session/me never fails with an HTTP error status
 * (docs/api/portal-api.yaml: `x-anonymous`, "this call itself bootstraps
 * the anonymous pre-session"): `kind: "anon"` is what "signed out" looks
 * like on the wire. A thrown error here is something else going wrong
 * entirely (the network, a proxy, a 5xx), handled by the "error" status
 * below, not by this helper.
 */
function statusFrom(me: Me): AuthStatus {
  return me.kind === "anon" ? "unauthenticated" : "authenticated";
}

function messageFrom(caught: unknown): string {
  return caught instanceof ApiError
    ? caught.message
    : "Could not reach the server. Check your connection and try again.";
}

interface AuthContextValue {
  status: AuthStatus;
  /** The current session (kind "anon" when signed out); null only before the first load, or on "error". */
  me: Me | null;
  /** Set when status is "error"; the reason GET /session/me itself failed. */
  error: string | null;
  /** Re-reads the session, for example right after login or a password change, or to retry after "error". */
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const apiClient = useApiClient();
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const session = await apiClient.me();
      setMe(session);
      setError(null);
      setStatus(statusFrom(session));
    } catch (caught) {
      setMe(null);
      setError(messageFrom(caught));
      setStatus("error");
    }
  }, [apiClient]);

  // Initial session load on mount. Deliberately not implemented as
  // `void refresh()`: the effect calls apiClient.me() itself and applies
  // the result behind an `ignore` guard, the pattern react.dev recommends
  // for data fetching in an effect (An unmount before the request settles
  // must not update state).
  useEffect(() => {
    let ignore = false;
    void (async () => {
      try {
        const session = await apiClient.me();
        if (!ignore) {
          setMe(session);
          setError(null);
          setStatus(statusFrom(session));
        }
      } catch (caught) {
        if (!ignore) {
          setMe(null);
          setError(messageFrom(caught));
          setStatus("error");
        }
      }
    })();
    return () => {
      ignore = true;
    };
  }, [apiClient]);

  const logout = useCallback(async () => {
    const response = await apiClient.logout();
    if (response.endSessionUrl) {
      // Leaving the SPA entirely for the IdP's own end-session page (ADR
      // 0008 section 3); there is no local session state left to update.
      window.location.assign(response.endSessionUrl);
      return;
    }
    const session = await apiClient.me();
    setMe(session);
    setError(null);
    setStatus(statusFrom(session));
  }, [apiClient]);

  const value = useMemo(
    () => ({ status, me, error, refresh, logout }),
    [status, me, error, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
